import {useEffect, useState} from 'react';
import {Link, useParams} from 'react-router-dom';
import Layout, {Caricamento, Titolo} from '../Layout';
import {
    Avvisi, BottoniDownload, Errore, FlagInviata, Indirizzo, StatoOrdine, dataOra, euro, gestisciErrore,
    getFattura, getOrdine, nominativo,
} from '../woo';
import {CambioStato, NoteOrdine, SpedizioneOrdine} from './OrdineScritture';

// Dettaglio di un ordine: testata, indirizzi, righe, pagamento, fattura elettronica POP e le
// scritture (stato, note, spedizione) in OrdineScritture.js.
export default function OrdinePage() {
    const {id} = useParams();
    const [risultato, setRisultato] = useState(null); // {ordine, avvisi}
    const [errore, setErrore] = useState('');
    const [versioneNote, setVersioneNote] = useState(0); // incrementata quando stato/spedizione creano una nota

    useEffect(() => {
        setRisultato(null);
        setErrore('');
        getOrdine(id).then(setRisultato).catch(gestisciErrore(setErrore));
    }, [id]);

    const o = risultato?.ordine;

    return (
        <Layout>
            <Titolo indietro>
                Ordine {o ? `#${o.number}` : id}
                {o && <small className="text-secondary fw-normal fs-6 ms-2">{dataOra(o.date_created)}</small>}
                {o && <span className="ms-3 fs-6 align-middle"><StatoOrdine stato={o.status}/></span>}
            </Titolo>
            <Errore messaggio={errore}/>
            {!errore && !o && <Caricamento/>}
            {o && (
                <>
                    <Avvisi avvisi={risultato.avvisi}/>
                    <div className="row g-3 mb-3">
                        <div className="col-12 col-md-4">
                            <div className="card h-100">
                                <div className="card-body">
                                    <h6 className="card-title">Cliente</h6>
                                    <div className="fw-semibold">{nominativo(o.billing) || 'n.d.'}</div>
                                    {o.customer_id > 0
                                        ? <Link to={`/clienti/${o.customer_id}`}>Scheda cliente #{o.customer_id}</Link>
                                        : <span className="text-secondary">Ordine ospite (non registrato)</span>}
                                    {o.billing?.email && <div className="mt-2"><a href={`mailto:${o.billing.email}`}>{o.billing.email}</a></div>}
                                    {o.billing?.phone && <div>{o.billing.phone}</div>}
                                </div>
                            </div>
                        </div>
                        <div className="col-12 col-md-4"><Indirizzo titolo="Fatturazione" indirizzo={o.billing}/></div>
                        <div className="col-12 col-md-4"><Indirizzo titolo="Spedizione" indirizzo={o.shipping}/></div>
                    </div>

                    <div className="card mb-3">
                        <div className="card-body">
                            <h6 className="card-title">Articoli</h6>
                            <Righe ordine={o}/>
                        </div>
                    </div>

                    <div className="row g-3 mb-3">
                        <div className="col-12 col-md-6">
                            <div className="card h-100">
                                <div className="card-body">
                                    <h6 className="card-title">Pagamento</h6>
                                    <Dato etichetta="Metodo" valore={o.payment_method_title || o.payment_method}/>
                                    <Dato etichetta="Pagato il" valore={dataOra(o.date_paid)}/>
                                    <Dato etichetta="Transazione" valore={o.transaction_id} mono/>
                                    <Dato etichetta="Ultima modifica" valore={dataOra(o.date_modified)}/>
                                    {o.customer_note && (
                                        <div className="mt-2">
                                            <div className="etichetta">Note del cliente</div>
                                            <div className="fst-italic">{o.customer_note}</div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="col-12 col-md-6">
                            <Fattura ordine={o}/>
                        </div>
                    </div>

                    <div className="row g-3 mb-3">
                        <div className="col-12 col-md-6">
                            <CambioStato ordine={o} onNoteCambiate={() => setVersioneNote((v) => v + 1)}
                                         onOrdineAggiornato={(ordine) => setRisultato((r) => ({...r, ordine}))}/>
                        </div>
                        <div className="col-12 col-md-6">
                            <SpedizioneOrdine ordine={o} onNoteCambiate={() => setVersioneNote((v) => v + 1)}/>
                        </div>
                    </div>
                    <NoteOrdine ordine={o} versione={versioneNote}/>

                    <AltriDati meta={o.meta_data}/>
                </>
            )}
        </Layout>
    );
}

function Dato({etichetta, valore, mono}) {
    if (!valore) return null;
    return (
        <div className="d-flex gap-2 mb-1">
            <span className="text-secondary" style={{minWidth: 120}}>{etichetta}</span>
            <span className={mono ? 'font-monospace' : ''}>{valore}</span>
        </div>
    );
}

function Righe({ordine: o}) {
    const righe = o.line_items ?? [];
    return (
        <div className="table-responsive">
            <table className="table table-hover mb-0">
                <thead>
                <tr>
                    <th>SKU</th>
                    <th>Articolo</th>
                    <th className="text-end">Qta</th>
                    <th className="text-end">Prezzo</th>
                    <th className="text-end">Totale</th>
                    <th className="text-end">IVA</th>
                </tr>
                </thead>
                <tbody>
                {righe.length === 0 && <tr><td colSpan={6} className="text-secondary">Nessuna riga</td></tr>}
                {righe.map((r) => (
                    <tr key={r.id}>
                        <td className="font-monospace">{r.sku || <span className="text-danger" title="Riga senza SKU">—</span>}</td>
                        <td>{r.name}</td>
                        <td className="text-end">{r.quantity}</td>
                        <td className="text-end text-nowrap">{euro(r.price, o.currency)}</td>
                        <td className="text-end text-nowrap">{euro(r.total, o.currency)}</td>
                        <td className="text-end text-nowrap">{euro(r.total_tax, o.currency)}</td>
                    </tr>
                ))}
                </tbody>
                <tfoot>
                <Totale etichetta="Sconto" valore={o.discount_total} valuta={o.currency} negativo/>
                <Totale etichetta="Spedizione" valore={o.shipping_total} valuta={o.currency}/>
                <Totale etichetta="IVA" valore={o.total_tax} valuta={o.currency}/>
                <tr className="fw-semibold">
                    <td colSpan={4} className="text-end">Totale ordine</td>
                    <td className="text-end text-nowrap">{euro(o.total, o.currency)}</td>
                    <td></td>
                </tr>
                </tfoot>
            </table>
        </div>
    );
}

function Totale({etichetta, valore, valuta, negativo}) {
    if (!Number(valore)) return null;
    return (
        <tr className="text-secondary">
            <td colSpan={4} className="text-end">{etichetta}</td>
            <td className="text-end text-nowrap">{negativo ? '-' : ''}{euro(valore, valuta)}</td>
            <td></td>
        </tr>
    );
}

// Fattura elettronica generata dal plugin POP per questo ordine (id fattura = id ordine).
function Fattura({ordine}) {
    const rif = {id: ordine.id, tipo: 'fattura'};
    const [doc, setDoc] = useState(null);      // dati documento
    const [stato, setStato] = useState('caricamento'); // caricamento | ok | assente | errore
    const [errore, setErrore] = useState('');

    useEffect(() => {
        setStato('caricamento');
        getFattura({id: ordine.id, tipo: 'fattura'})
            .then((d) => { setDoc(d); setStato('ok'); })
            .catch((err) => {
                if (err.status === 404) return setStato('assente');
                setStato('errore');
                gestisciErrore(setErrore)(err);
            });
    }, [ordine.id]);

    return (
        <div className="card h-100">
            <div className="card-body">
                <h6 className="card-title">Fattura elettronica</h6>
                {stato === 'caricamento' && <Caricamento/>}
                {stato === 'assente' && <span className="text-secondary">Nessuna fattura generata per questo ordine.</span>}
                {stato === 'errore' && <span className="text-danger small">{errore}</span>}
                {stato === 'ok' && doc && (
                    <>
                        <Dato etichetta="Numero" valore={doc.invoice_number}/>
                        <Dato etichetta="Tipo" valore={doc.choice_type === 'receipt' ? 'Ricevuta' : doc.choice_type === 'invoice' ? 'Fattura' : doc.choice_type}/>
                        <Dato etichetta="Cliente" valore={doc.customer_type}/>
                        <Dato etichetta="Totale" valore={euro(doc.total, doc.currency)}/>
                        <Dato etichetta="Completato il" valore={dataOra(doc.date_completed)}/>
                        <div className="d-flex gap-2 mb-1">
                            <span className="text-secondary" style={{minWidth: 120}}>Invio SdI</span>
                            <FlagInviata rif={rif} documento={doc} onAggiornato={setDoc}/>
                        </div>
                        <div className="mt-3"><BottoniDownload rif={rif}/></div>
                        {ordine.status === 'processing' && (
                            <p className="text-secondary small mt-2 mb-0">L'XML viene generato al completamento dell'ordine: in lavorazione potrebbe non essere ancora disponibile.</p>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

// Meta dell'ordine (campi fiscali del checkout, plugin, ...): solo i valori semplici.
function AltriDati({meta}) {
    const righe = (meta ?? []).filter((m) => m.value !== null && typeof m.value !== 'object' && String(m.value) !== '');
    if (righe.length === 0) return null;
    return (
        <details className="card">
            <summary className="card-body py-2 text-secondary small">Altri dati dell'ordine ({righe.length})</summary>
            <div className="card-body pt-0">
                <table className="table table-sm mb-0 small">
                    <tbody>
                    {righe.map((m) => (
                        <tr key={m.id}>
                            <td className="font-monospace text-secondary">{m.key}</td>
                            <td>{String(m.value)}</td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </details>
    );
}
